import { GridState } from 'ag-grid-community';
import { useMemo } from 'react';
import pick from 'lodash/pick';

interface VersionedGridSettings {
  version?: number;
  state: any;
}

interface SettingsData {
  [key: string]: any | VersionedGridSettings;
}

const agGridSettingsStoreKey = 'ag-grid-settings-tcs-admin-ui';

export const getGridSettings = (gridName: string, expectedVersion?: number): any => {
  const data = localStorage.getItem(agGridSettingsStoreKey);
  try {
    const gridSettingsData = JSON.parse(data ?? '{}');
    const currentGridSetting = gridSettingsData?.[gridName];

    if (currentGridSetting !== undefined) {
      // Backwards compatibility: if it's not a versioned object, use it as-is
      if (typeof currentGridSetting !== 'object' || !('state' in currentGridSetting)) {
        // Old format without version - if version is expected, invalidate cache
        if (expectedVersion !== undefined) {
          console.log(`Grid "${gridName}": Expected version ${expectedVersion} but found old format. Clearing cache.`);
          return undefined;
        }
        // No version expected, return old format (backwards compatible)
        return currentGridSetting;
      }

      // New format with version
      const versionedSetting = currentGridSetting as VersionedGridSettings;

      // Check version match if version is expected
      if (expectedVersion !== undefined && versionedSetting.version !== expectedVersion) {
        console.log(`Grid "${gridName}": Version mismatch. Expected: ${expectedVersion}, Got: ${versionedSetting.version}. Clearing cache.`);
        return undefined;
      }

      return versionedSetting.state;
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
};

const GRID_STATE_PERSIST_WHITELIST: (keyof GridState)[] = [
  'columnOrder',
  'columnPinning',
  'columnSizing',
  'columnVisibility',
];

export const setGridSettings = (gridName: string, value: GridState, version?: number) => {
  const data = localStorage.getItem(agGridSettingsStoreKey);

  const setStorageData = (data: SettingsData) => {
    const persistedGridState = pick(value, GRID_STATE_PERSIST_WHITELIST);

    // If version is provided, use new versioned format
    if (version !== undefined) {
      data[gridName] = {
        version,
        state: persistedGridState,
      } as VersionedGridSettings;
    } else {
      // No version provided, maintain backwards compatibility
      data[gridName] = persistedGridState;
    }

    const json = JSON.stringify(data);
    localStorage.setItem(agGridSettingsStoreKey, json);
  };

  try {
    const gridToggleData = JSON.parse(data ?? '{}');
    setStorageData(gridToggleData);
  } catch (e) {
    setStorageData({});
  }
};

export const useAgLocalStorage = (gridName: string, version?: number) => {
  const gridSettings = useMemo(() => {
    return getGridSettings(gridName, version);
  }, [gridName, version]);
  return gridSettings;
};
